import { differenceInMinutes } from 'date-fns';
import doDateRangesOverlap from '@sdk/utils/doDateRangesOverlap';

import {
  TimeClockEntryWithDates,
  BreakEntry,
  BreakEntryWithDates,
  MaybeClockInClockOut,
  MaybeStartEnd,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import { getClockInClockOutDates } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.utils';

/**
 * Handle date parsing after hours of clockIn and clockOut are selected.
 * This means if you choose clockOut time that is earlier than clockIn
 * you could use this function to make clockOut the next day.
 *
 * @param breakEntries TimeClockEntry
 * @param date Date
 * @returns BreakEntryWithDates[]
 */
export const getBreakEntriesWithDates = (
  breakEntries: BreakEntry[],
  clockInDate: Date
): BreakEntryWithDates[] =>
  breakEntries.map(({ start, end, ...rest }) => {
    const { clockIn: startDate, clockOut: endDate } = getClockInClockOutDates(
      clockInDate,
      start,
      end,
      { isDateWithTime: true }
    );

    return {
      ...rest,
      start: startDate,
      end: endDate,
    };
  });

/**
 * This method scan multiple timeclock entries and flatten the list of breaks to be only one single list.
 *
 * @param timeClockEntries
 * @returns BreakEntryWithDates[]
 */
export const getAllBreakEntriesWithDates = (
  timeClockEntries: TimeClockEntryWithDates[]
): BreakEntryWithDates[] => {
  const allBreakEntries = [];
  timeClockEntries.map(({ breaks }) => {
    allBreakEntries.push(...breaks);
  });
  return allBreakEntries;
};

/**
 * Function checks if break entry start and end times are within the boundaries set by clock in and clock out times.
 * Returns true if it is, which means such break entry is **invalid**.
 *
 * For entries that are incomplete (missing clockIn, clockOut dates) it returns false.
 *
 * @param breakEntry
 * @param timeClockEntry
 * @returns boolean
 */
export const isBeyondTimesheetRange = (
  { start, end }: MaybeStartEnd,
  { clockIn, clockOut }: MaybeClockInClockOut
) => {
  if (!clockIn || !clockOut || !start || !end) {
    return false;
  }

  const startTimestamp = start.getTime();
  const endTimestamp = end.getTime();
  const clockInTimestamp = clockIn.getTime();
  const clockOutTimestamp = clockOut.getTime();

  return (
    startTimestamp < clockInTimestamp ||
    startTimestamp > clockOutTimestamp ||
    endTimestamp > clockOutTimestamp
  );
};

/**
 * Function checks if one of multiple breaks is not starting while the previous one is still in progress.
 *
 * @param breakEntry
 * @param allBreakEntries
 * @returns boolean
 */
export const isBreakOverlappingWithOtherBreaks = (
  breakEntry: BreakEntryWithDates,
  allBreakEntries: BreakEntryWithDates[]
) => {
  return allBreakEntries.some((currentEntry) => {
    if (currentEntry.key === breakEntry.key) {
      return false;
    }

    return doDateRangesOverlap(
      { from: breakEntry.start, to: breakEntry.end },
      { from: currentEntry.start, to: currentEntry.end },
      { allowAdjacent: true }
    );
  });
};

export const getTotalBreaksDurationInMinutes = (breakEntries: MaybeStartEnd[]): number =>
  breakEntries.reduce((acc, { start, end }) => {
    const value = !start || !end ? 0 : differenceInMinutes(end, start);
    return acc + value;
  }, 0);
